import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import Section from '../../../components/Section';
import { Table, Button, Modal, Form } from 'semantic-ui-react';
import moment from 'moment';
import { Editor } from '@tinymce/tinymce-react';
import DatePicker from '../../../components/DatePicker';

const AdvertisementsSection = ({ actions }) => {
	const [isValid, setIsValid] = useState(false);
	const editorRef = useRef(null);

	const emptyAd = useMemo(() => {
		return {
			activeOn: '',
			header: '',
			size: 0,
			bodyHtml: 'Default Body',
		};
	}, []);

	const [modalState, setModalState] = useState({
		isOpen: false,
		isEdit: false,
		ad: emptyAd,
	});

	const [advertisements, setAdvertisements] = useState([]);

	const load = useCallback(async () => {
		var result = await actions.getAllAdvertisements();
		setAdvertisements(result.response);
	}, [actions]);

	useEffect(() => {
		const _load = async () => {
			await load();
		};

		_load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onChange = useCallback(
		(_, { name, value }) => {
			setModalState({ ...modalState, ad: { ...modalState.ad, [name]: value } });
		},
		[modalState]
	);

	const sizeOptions = [
		{ text: 'Tiny', value: 0 },
		{ text: 'Mini', value: 1 },
		{ text: 'Small', value: 2 },
		{ text: 'Large', value: 3 },
	];

	const onSave = useCallback(async () => {
		try {
			modalState.ad.bodyHtml = editorRef.current.getContent();
			debugger;
			if (modalState.isEdit) {
				await actions.updateAdvertisement(modalState.ad);
			} else {
				await actions.addAdvertisement(modalState.ad);
			}

			await load();
			setModalState({ isOpen: false, ad: emptyAd });
		} catch {
			alert('Failed to save');
		}
	}, [actions, emptyAd, load, modalState]);

	const updateIsValid = useCallback(() => {
		if (editorRef.current) {
			const editorVal = editorRef.current.getContent();
			setIsValid(modalState.ad.header !== '' && editorVal !== '');
		} else {
			setIsValid(false);
		}
	}, [modalState.ad]);

	useEffect(() => {
		updateIsValid();
	}, [updateIsValid]);

	const onDelete = useCallback(
		async (id) => {
			if (window.confirm('Are you sure?')) {
				await actions.removeAdvertisement(id);
				await load();
			}
		},
		[actions, load]
	);

	const onEdit = useCallback((ad) => {
		setModalState({ isOpen: true, isEdit: true, ad });
		if (editorRef.current) {
			editorRef.current.setContent(ad.bodyHtml);
		}
	}, []);

	const onReset = useCallback(async () => {
		if (window.confirm('Are you sure?')) {
			await actions.resetAdvertisement();
		}
	}, [actions]);

	return (
		<Section title='Advertisements' color='red'>
			<Modal open={modalState.isOpen}>
				<Modal.Header>{modalState.isEdit ? 'Edit ' : 'New '}Advertisement</Modal.Header>
				<Modal.Content>
					{modalState.ad && (
						<Form>
							<Form.Input label='Header' name='header' value={modalState.ad.header} onChange={onChange} />
							<Form.Dropdown label='Size' name='size' value={modalState.ad.size} onChange={onChange} options={sizeOptions} />
							<Form.Field>
								<label>Active On</label>
								<DatePicker name='activeOn' selected={modalState.ad.activeOn ? Date.parse(moment(modalState.ad.activeOn).format()) : null} onChange={onChange} />
							</Form.Field>
							<Form.Field>
								<label>Body</label>
								<Editor
									onInit={(_, editor) => (editorRef.current = editor)}
									tinymceScriptSrc={'/tinymce/tinymce.min.js'}
									initialValue={modalState.ad.bodyHtml}
									onChange={() => updateIsValid()}
									init={{
										height: 500,
										menubar: false,
										plugins: ['advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen', 'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'],
										toolbar: 'undo redo | code | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat',
										content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
									}}
								/>
							</Form.Field>
						</Form>
					)}
				</Modal.Content>
				<Modal.Actions>
					<Button onClick={() => setModalState({ isOpen: false, ad: emptyAd })}>Cancel</Button>
					<Button primary onClick={onSave} disabled={!isValid}>
						Save
					</Button>
				</Modal.Actions>
			</Modal>

			<Table celled>
				<Table.Header fullWidth>
					<Table.Row>
						<Table.HeaderCell>Header</Table.HeaderCell>
						<Table.HeaderCell>Size</Table.HeaderCell>
						<Table.HeaderCell>Active On</Table.HeaderCell>
						<Table.HeaderCell>Actions</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{advertisements.map((ad, i) => (
						<Table.Row key={i}>
							<Table.Cell>{ad.header}</Table.Cell>
							<Table.Cell>{sizeOptions.find((so) => so.value === ad.size)?.text}</Table.Cell>
							<Table.Cell>{moment(ad.activeOn).format('LL')}</Table.Cell>
							<Table.Cell>
								<Button onClick={() => onEdit(ad)}>Edit</Button>
								<Button onClick={() => onDelete(ad.id)} icon='trash' negative />
							</Table.Cell>
						</Table.Row>
					))}
				</Table.Body>
				<Table.Footer fullWidth>
					<Table.Row>
						<Table.HeaderCell>
							<Button onClick={onReset}>Reset 'Hide Advertisement'</Button>
						</Table.HeaderCell>
						<Table.HeaderCell colSpan='3'>
							<Button primary floated='right' onClick={() => setModalState({ isOpen: true, isEdit: false, ad: emptyAd })}>
								Add
							</Button>
						</Table.HeaderCell>
					</Table.Row>
				</Table.Footer>
			</Table>
		</Section>
	);
};

export default AdvertisementsSection;
